import { useState, useEffect } from 'react';
import { getCookie } from './cookies';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FaEnvelope, 
  FaLock, 
  FaSignInAlt, 
  FaUserPlus, 
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaSpinner
} from 'react-icons/fa';
import { toast } from 'react-toastify';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear specific field error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: []
      });
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      const sessionid = getCookie('csrftoken');
      if (sessionid) {
        try {
          const response = await axios.get('/api/check_auth/');
          if (response.data.authenticated) {
            navigate('/');
          }
        } catch (error) {
          // If there's an error checking authentication, we'll just continue with the login page
          console.error('Error checking authentication:', error);
        }
      }
    };
    
    checkSession();
  }, [navigate]);

  function deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);

    deleteCookie('sessionid');
    
    try {
      await axios.post('/api/auth/login/', formData);
      toast.success('Login successful!');
      navigate('/');
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/');
        return;
      }
      
      console.error('Login error:', err.response);
      
      if (err.response?.data) {
        setErrors(err.response.data);
        
        // Check for non-field errors and display them with toast
        if (err.response.data.non_field_errors) {
          err.response.data.non_field_errors.forEach(error => {
            toast.error(error);
          });
        }
      } else {
        toast.error('An error occurred during login. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderErrors = (fieldName) => {
    if (!errors[fieldName] || errors[fieldName].length === 0) return null;
    
    return (
      <div className="mt-1">
        {errors[fieldName].map((error, index) => (
          <p key={index} className="text-red-500 text-xs flex items-start mt-1">
            <FaExclamationTriangle className="mr-1 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-secondary to-secondary-dark p-6 text-center">
            <h2 className="text-2xl font-bold text-white flex items-center justify-center">
              <FaSignInAlt className="mr-2" />
              Sign In to Your Account
            </h2>
            <p className="mt-2 text-blue-100">
              Access your dashboard and submissions
            </p>
          </div>
          
          {/* Form */}
          <div className="p-6">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    id="email" 
                    name="email" 
                    type="email" 
                    autoComplete="email" 
                    required 
                    className={`block w-full pl-10 pr-3 py-2 border ${
                      errors.email ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-secondary focus:border-secondary`}
                    placeholder="you@example.com" 
                    value={formData.email} 
                    onChange={handleChange} 
                  />
                </div>
                {renderErrors('email')}
              </div>
              
              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    id="password" 
                    name="password" 
                    type={showPassword ? "text" : "password"} 
                    autoComplete="current-password" 
                    required 
                    className={`block w-full pl-10 pr-10 py-2 border ${
                      errors.password ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-secondary focus:border-secondary`}
                    placeholder="Your password" 
                    value={formData.password} 
                    onChange={handleChange} 
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                {renderErrors('password')}
              </div>
              
              {/* Non-field errors */}
              {errors.non_field_errors && (
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaExclamationTriangle className="h-5 w-5 text-red-500" />
                    </div>
                    <div className="ml-3">
                      {errors.non_field_errors.map((error, index) => (
                        <p key={index} className="text-sm text-red-700 dark:text-red-300">{error}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Submit Button */}
              <div>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <FaSignInAlt className="mr-2" />
                      Sign in
                    </>
                  )}
                </button>
              </div>
            </form>
            
            <div className="mt-6 flex items-center justify-center">
              <div className="text-sm">
                <span className="text-gray-500 dark:text-gray-400 mr-1">
                  Don't have an account?
                </span>
                <button 
                  type="button"
                  onClick={() => navigate("/register")}
                  className="font-medium text-secondary hover:text-secondary-dark dark:text-secondary-light dark:hover:text-secondary flex items-center"
                >
                  <FaUserPlus className="mr-1" />
                  Register now
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Additional Help */}
        <div className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          Having trouble logging in? Contact your hackathon administrator.
        </div>
      </div>
    </div>
  );
}

export default Login;